import { useEffect, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Grid2, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import {
  IntegrationSettingsAccountLevelForm,
  IntegrationSettingsEntry,
  IntegrationSettingsForm,
} from 'modules/integrationSettings/types';

const DEALERS_QUERY = gql`
  query vendorProfilesByDynamicsLenderAccountId($dynamicsAccountId: ID!) {
    vendorProfilesByDynamicsLenderAccountId(dynamicsAccountId: $dynamicsAccountId) {
      id
      name
      accountId
      dynamicsAccountId
    }
  }
`;

const CIT_PRIMARY = 'dealerId';
const WELLS_PRIMARY = 'partnerDealerId';
const CIT_COMPANION = 'externalVendorId';

interface DealerOption {
  accountId: string;
  vendorProfileId: string;
  displayName: string;
  value: string;
}

interface Props {
  integrationSettings: IntegrationSettingsEntry[];
  tableData: IntegrationSettingsAccountLevelForm[];
}

export const SyndicatedAccountSettings = ({ integrationSettings, tableData }: Props) => {
  const account = useSelector((state: any) => state.account);
  const vendorProfile = useSelector((state: any) => state.vp);

  const accountId = tableData[0]?.accountId;
  const isCIT = integrationSettings.some(setting => setting.name === CIT_PRIMARY);
  const primaryName = isCIT ? CIT_PRIMARY : WELLS_PRIMARY;
  const primaryValueField = isCIT ? CIT_PRIMARY : 'value';
  const primaryLabel = integrationSettings.find(setting => setting.name === primaryName)?.displayName || 'Dealer ID';
  const externalLabel =
    integrationSettings.find(setting => setting.name === CIT_COMPANION)?.displayName || 'External Vendor ID';

  const fieldName = `account.${accountId}.integrationSettingsValues.${primaryName}`;

  const { control, setValue, register, getValues } = useFormContext<IntegrationSettingsForm>();
  const { fields, append, remove } = useFieldArray({ control, name: fieldName as never });
  const watchedRows = (useWatch({ control, name: fieldName as never }) as Record<string, string>[]) || [];

  // Syndicated ISVs are arrays; guard against a legacy scalar value at this path.
  useEffect(() => {
    const current = getValues(fieldName as never);
    if (current !== undefined && !Array.isArray(current)) {
      setValue(fieldName as never, [] as never);
    }
  }, [fieldName, getValues, setValue]);

  const { data: dealerData } = useQuery(DEALERS_QUERY, {
    skip: !account?.dynamicsAccountId,
    variables: { dynamicsAccountId: account?.dynamicsAccountId },
    fetchPolicy: 'no-cache',
    context: { authRequired: true },
  });

  const dealerOptions = useMemo<DealerOption[]>(() => {
    const currentAccountOption: DealerOption | null = account?.id
      ? {
          accountId: account.id,
          vendorProfileId: vendorProfile?.id || '',
          displayName: (account.name || '').replace(/^VP - /, ''),
          value: account.dynamicsAccountId || '',
        }
      : null;

    const queriedOptions = (dealerData?.vendorProfilesByDynamicsLenderAccountId || []).map((dealer: any) => ({
      accountId: dealer.accountId,
      vendorProfileId: dealer.id,
      displayName: (dealer.name || '').replace(/^VP - /, ''),
      value: dealer.dynamicsAccountId,
    }));

    return [...(currentAccountOption ? [currentAccountOption] : []), ...queriedOptions]
      .filter((option, index, options) => options.findIndex(item => item.accountId === option.accountId) === index)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [account, vendorProfile, dealerData]);

  const resolveAccountId = (row: Record<string, string>) =>
    row?.accountId || dealerOptions.find(option => option.vendorProfileId === row?.vendorProfileId)?.accountId || '';

  const selectDealer = (index: number, selectedAccountId: string) => {
    const dealer = dealerOptions.find(option => option.accountId === selectedAccountId);
    setValue(`${fieldName}.${index}.accountId` as never, (dealer?.accountId || '') as never);
    setValue(`${fieldName}.${index}.vendorProfileId` as never, (dealer?.vendorProfileId || '') as never);
    setValue(`${fieldName}.${index}.displayName` as never, (dealer?.displayName || '') as never);
  };

  const addRow = () =>
    append(
      (isCIT
        ? { value: '', dealerId: '', externalVendorId: '', displayName: '', accountId: '', vendorProfileId: '' }
        : { value: '', displayName: '', accountId: '', vendorProfileId: '' }) as never
    );

  return (
    <Stack gap={2}>
      <Typography variant="subtitle1" fontWeight={600}>
        Dealer Settings
      </Typography>

      {fields.map((field, index) => {
        const selectedAccountId = resolveAccountId(watchedRows[index]);
        const usedAccountIds = watchedRows
          .filter((_, rowIndex) => rowIndex !== index)
          .map(row => resolveAccountId(row));
        const availableDealers = dealerOptions.filter(
          dealer => dealer.accountId === selectedAccountId || !usedAccountIds.includes(dealer.accountId)
        );

        return (
          <Grid2
            key={field.id}
            container
            spacing={2}
            alignItems="center"
            sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}
          >
            <Grid2 size={isCIT ? 4 : 6}>
              <TextField
                select
                fullWidth
                label="Dealer Account"
                value={selectedAccountId}
                onChange={event => selectDealer(index, event.target.value)}
              >
                {availableDealers.length === 0 && selectedAccountId && (
                  <MenuItem value={selectedAccountId}>{watchedRows[index]?.displayName}</MenuItem>
                )}
                {availableDealers.map(dealer => (
                  <MenuItem key={dealer.accountId} value={dealer.accountId}>
                    {dealer.displayName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid2>

            <Grid2 size={isCIT ? 4 : 5}>
              <TextField
                fullWidth
                label={primaryLabel}
                defaultValue={(field as Record<string, string>)[primaryValueField] || ''}
                {...register(`${fieldName}.${index}.${primaryValueField}` as never)}
              />
            </Grid2>

            {isCIT && (
              <Grid2 size={3}>
                <TextField
                  fullWidth
                  label={externalLabel}
                  defaultValue={(field as Record<string, string>)[CIT_COMPANION] || ''}
                  {...register(`${fieldName}.${index}.${CIT_COMPANION}` as never)}
                />
              </Grid2>
            )}

            <Grid2 size={1} display="flex" justifyContent="flex-end">
              <IconButton aria-label="delete" color="error" onClick={() => remove(index)}>
                <DeleteIcon />
              </IconButton>
            </Grid2>
          </Grid2>
        );
      })}

      {fields.length === 0 && <Typography color="text.secondary">No dealers configured.</Typography>}

      <Box>
        <Button startIcon={<AddIcon />} onClick={addRow} variant="outlined">
          Add Dealer
        </Button>
      </Box>
    </Stack>
  );
};
